import { useTranslation } from '@spcc/core'
import { Checkbox, Tabs, TabsList, TabsTrigger } from '@spcc/uikit'
import { SubCustomerMultiSelect } from '@spcc/domains/sub-customers'
import type { OrderSubCustomerPresence } from '../../../../domain/types'
import { ORDER_SUB_CUSTOMER_PRESENCE } from '../../../../domain/constants'

/** Tabs need a string value for "all orders". */
const ALL = 'all'

const PRESENCE_TABS = [
  { value: ALL, labelKey: 'orders.subCustomerPresenceAll' },
  { value: ORDER_SUB_CUSTOMER_PRESENCE.ASSIGNED, labelKey: 'orders.subCustomerPresenceAssigned' },
  { value: ORDER_SUB_CUSTOMER_PRESENCE.NONE, labelKey: 'orders.subCustomerPresenceNone' },
] as const

export interface SubCustomerFilterValue {
  subCustomerPresence: OrderSubCustomerPresence | undefined
  unknownSubCustomer: boolean | undefined
  subCustomerIds: string[]
}

interface SubCustomerFilterSectionProps {
  value: SubCustomerFilterValue
  onChange: (patch: Partial<SubCustomerFilterValue>) => void
}

/**
 * Orders by sub-customer: with / without one, "Unknown customer" only, and any
 * number of specific sub-customers picked by server search.
 * "No customer" rules the other two out, so they are hidden (and cleared) then.
 */
export function SubCustomerFilterSection({ value, onChange }: SubCustomerFilterSectionProps) {
  const { t } = useTranslation()
  const noSubCustomer = value.subCustomerPresence === ORDER_SUB_CUSTOMER_PRESENCE.NONE
  const setPresence = (next: string) => {
    const presence = next === ALL ? undefined : (next as OrderSubCustomerPresence)
    const clearsOthers = presence === ORDER_SUB_CUSTOMER_PRESENCE.NONE
    onChange(clearsOthers ? { subCustomerPresence: presence, unknownSubCustomer: undefined, subCustomerIds: [] } : { subCustomerPresence: presence })
  }

  return (
    <div className="space-y-4">
      <Tabs value={value.subCustomerPresence ?? ALL} onValueChange={setPresence}>
        <TabsList className="h-11 w-full sm:h-9">
          {PRESENCE_TABS.map(({ value: tab, labelKey }) => (
            <TabsTrigger key={tab} value={tab} className="px-2 text-xs">{t(labelKey)}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {!noSubCustomer && (
        <>
          <label className="flex min-h-11 cursor-pointer items-center gap-2">
            <Checkbox
              checked={value.unknownSubCustomer === true}
              onCheckedChange={(checked) => onChange({ unknownSubCustomer: checked === true || undefined })}
            />
            <span className="text-sm">{t('orders.unknownSubCustomerOnly')}</span>
          </label>
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">{t('orders.specificSubCustomers')}</p>
            <SubCustomerMultiSelect value={value.subCustomerIds} onChange={(ids) => onChange({ subCustomerIds: ids })} />
          </div>
        </>
      )}
    </div>
  )
}
